// ChatRuntimeProvider wires the real merged entity/store/page symbols into
// the ChatRuntime injection contract and mounts the chat workspace.
// Simplified for the assistant-ui rewrite: the API client is now consumed
// directly (useNativeAgenthubExternalRuntime builds the real assistant-ui runtime from it)
// instead of being re-wrapped into a bespoke ChatApi shape.

import { useEffect, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createThreadApi, invalidateThreadList } from '@/entities/thread';
import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import { useThreadsQuery, useThreadMessagesQuery } from '@/entities/thread';
import { createChatApi } from '@/entities/chat';
import { ChatPageLayout } from '@/pages/chat/ChatPageLayout';
import { ChatWorkspace } from '@/widgets/chat-workspace';
import { useCapabilities } from '@/app/providers/CapabilitiesProvider';
import type { ThreadSelection } from '@/features/chat/thread-history';
import type { ChatRuntime } from '@/shared/chat-runtime';
import {
  useChatLockStore,
  useChatWorkspaceController,
  useResolvedThreadId,
} from '@/shared/chat-workspace';

type ChatRuntimeProviderProps = {
  agentId: string;
  initialThreadId?: string | null;
  layout?: 'page' | 'embedded';
  hideThreadSidebar?: boolean;
};

function PassthroughLayout({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

const authProvider = { getToken };

export function ChatRuntimeProvider({
  agentId,
  initialThreadId,
  layout = 'page',
  hideThreadSidebar = false,
}: ChatRuntimeProviderProps) {
  const queryClient = useQueryClient();
  const baseUrl = useMemo(() => readEnv('VITE_API_BASE_URL') ?? '', []);
  const api = useMemo(() => {
    const client = createApiClient({ baseUrl, auth: authProvider });
    const entityApi = createChatApi({ client });
    const threadApi = createThreadApi(client);
    return {
      ...entityApi,
      generateTitle: async (threadId: string) => {
        try {
          await threadApi.generateTitle(threadId);
          await invalidateThreadList(queryClient);
        } catch {
          // Title generation is best-effort — never surface to the user.
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryClient is stable
  }, [baseUrl]);

  const { selectThread, startNewChat } = useChatWorkspaceController();

  const { capabilities } = useCapabilities();
  const streamTimeoutMs = (capabilities?.timeouts?.ui_stream_timeout_seconds ?? 3600) * 1000;
  const resolvedThreadId = useResolvedThreadId(agentId);
  const activeThreadId = resolvedThreadId ?? initialThreadId ?? null;

  useEffect(() => {
    if (!activeThreadId) return undefined;
    const lockStore = useChatLockStore.getState();
    lockStore.acquireLock(activeThreadId);
    return () => {
      useChatLockStore.getState().releaseLock(activeThreadId);
    };
  }, [activeThreadId]);

  const threadSelection = useMemo((): ThreadSelection => {
    return {
      threadId: activeThreadId,
      selectThread: (next) => {
        if (next === null) {
          void startNewChat(agentId);
        } else {
          selectThread(agentId, next);
        }
      },
    };
  }, [activeThreadId, agentId, selectThread, startNewChat]);

  const runtime: ChatRuntime = useMemo(
    () => ({
      pageShell: {
        Layout: layout === 'embedded' ? PassthroughLayout : ChatPageLayout,
      },
      threadQueries: {
        useThreadsQuery,
        useThreadMessagesQuery,
      },
      api,
      streamTimeoutMs,
      baseUrl,
    }),
    [api, layout, streamTimeoutMs, baseUrl],
  );

  return (
    <ChatWorkspace
      agentId={agentId}
      runtime={runtime}
      threadSelection={threadSelection}
      hideThreadSidebar={hideThreadSidebar}
    />
  );
}
